import {
  createPopup,
  createWidget,
  PopupOptions,
  WidgetOptions,
} from '@typeform/embed';

import {PROJECT_TAGS} from '@/constants';
import {getUserTrack, useTrackingManager} from '@/hooks';
import useWindowSize from '@/hooks/WindowSize/WindowSize';
import {isProduction} from '@/utils/environment';

import {
  ITypeFormCreatePopup,
  ITypeFormCreateWidget,
  ITypeFormEvent,
} from './models/interfaces';

import './styles';

export const TypeFormService = () => {
  //#region [---- [PROPERTIES] ----]
  const {trackEvent} = useTrackingManager();
  const {isMobile} = useWindowSize();
  const userTrack = getUserTrack();
  const enableSandbox = !isProduction;
  //#endregion

  //#region [---- [LOGIC] ----]
  const openPopup = (createPopupProps: ITypeFormCreatePopup) => {
    const popupContainer = document.createElement('div');
    popupContainer.className = 'type-form-popup-container';
    document.body.appendChild(popupContainer);

    const closeButton = document.createElement('button');
    closeButton.className = 'type-form-close-button';
    closeButton.innerHTML = '&times;';
    popupContainer.appendChild(closeButton);

    closeButton.onclick = () => {
      popup.close();
      if (document.body.contains(popupContainer)) {
        document.body.removeChild(popupContainer);
      }
    };

    const popup = createPopup(createPopupProps.id, {
      size: createPopupProps.size,
      enableSandbox: enableSandbox,
      autoClose: createPopupProps.autoClose,
      fullScreen: true,
      onClose: () => {
        trackTypeFormEvent(createPopupProps.events.onCloseEvent);
        if (document.body.contains(popupContainer)) {
          document.body.removeChild(popupContainer);
        }
      },
      onReady: () => {
        const iframe = popupContainer.querySelector('iframe');
        if (iframe) {
          iframe.style.width = '100vw';
          iframe.style.height = '100vh';
          iframe.style.padding = !isMobile ? '48px 106px' : '0px 0px';
          iframe.style.zIndex = '1000';
        }
        trackTypeFormEvent(createPopupProps.events.onReadyEvent);
      },
      onSubmit: () => {
        trackTypeFormEvent(createPopupProps.events.onSubmitEvent);
        popup.close();
        createPopupProps?.afterSubmit && createPopupProps.afterSubmit();
      },
      hidden: {
        ...userTrack,
      },
      container: popupContainer,
      className: 'type-form-popup',
    } as PopupOptions);
    popup.open();
  };

  const loadWidget = (createWidgetProps: ITypeFormCreateWidget) => {
    const widget = createWidget(createWidgetProps.id, {
      enableSandbox: enableSandbox,
      container: createWidgetProps.container,
      onReady: () => {
        trackTypeFormEvent(createWidgetProps.events.onReadyEvent);
      },
      onSubmit: () => {
        trackTypeFormEvent(createWidgetProps.events.onSubmitEvent);
        if (createWidgetProps.callbacks) {
          createWidgetProps.callbacks.onSubmitCallback();
        }
      },
      hidden: {
        ...userTrack,
      },
    } as WidgetOptions);
    return widget;
  };

  const trackTypeFormEvent = (event: ITypeFormEvent) => {
    trackEvent(event.eventName, {
      project_tag: PROJECT_TAGS.TYPE_FORM,
      event: event.eventData,
    });
  };
  //#endregion

  return {
    openPopup,
    loadWidget,
  };
};
